import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import {
  getITSpareParts, createITSparePart, updateITSparePart, deleteITSparePart, createInventoryMovement,
} from '../../lib/api';
import { getITErrorMessage } from '../../lib/itErrors';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Package, Plus, PencilSimple, Trash, ArrowsLeftRight, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const MOVEMENT_TYPES = [
  { code: 'entrada', name: 'Entrada (compra / devolución)' },
  { code: 'salida', name: 'Salida (uso en servicio)' },
  { code: 'ajuste_positivo', name: 'Ajuste positivo' },
  { code: 'ajuste_negativo', name: 'Ajuste negativo' },
];

const EMPTY_PART = { name: '', sku: '', category: '', stock: 0, minimum_stock: 1, unit_cost: 0, location: '', notes: '' };
const EMPTY_MOVEMENT = { movement_type: 'entrada', quantity: 1, reference: '', notes: '' };

const toList = (data) => (Array.isArray(data) ? data : (data?.items || []));
const inputClass = 'bg-[#12141a] border-gray-700 text-white';
const isLowStock = (part) => (Number(part.stock) || 0) <= (Number(part.minimum_stock) || 0);

export default function SparePartsPage() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [movementPart, setMovementPart] = useState(null);
  const [movementForm, setMovementForm] = useState(EMPTY_MOVEMENT);
  const [search, setSearch] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [form, setForm] = useState(EMPTY_PART);

  const loadParts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getITSpareParts(lowStockOnly ? { low_stock_only: true } : {});
      setParts(toList(res.data));
    } catch (err) {
      toast.error(getITErrorMessage(err, 'Error al cargar los repuestos'));
    } finally {
      setLoading(false);
    }
  }, [lowStockOnly]);

  useEffect(() => { loadParts(); }, [loadParts]);

  const visibleParts = parts.filter((part) => {
    const term = search.trim().toLowerCase();
    return !term || [part.name, part.sku, part.category, part.location].some((value) => (value || '').toLowerCase().includes(term));
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_PART);
    setShowModal(true);
  };

  const openEdit = (part) => {
    setEditing(part);
    setForm({
      name: part.name || '', sku: part.sku || '', category: part.category || '', stock: part.stock ?? 0,
      minimum_stock: part.minimum_stock ?? 0, unit_cost: part.unit_cost ?? 0, location: part.location || '', notes: part.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return toast.error('El nombre es obligatorio');
    const payload = {
      name: form.name.trim(),
      sku: form.sku.trim() || null,
      category: form.category.trim() || null,
      minimum_stock: parseInt(form.minimum_stock, 10) || 0,
      unit_cost: parseFloat(form.unit_cost) || 0,
      location: form.location.trim() || null,
      notes: form.notes.trim() || null,
    };
    setSaving(true);
    try {
      if (editing) {
        // Stock only changes through movements so the history stays consistent.
        await updateITSparePart(editing._id, payload);
        toast.success('Repuesto actualizado');
      } else {
        await createITSparePart({ ...payload, stock: parseInt(form.stock, 10) || 0 });
        toast.success('Repuesto creado');
      }
      setShowModal(false);
      loadParts();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'Error al guardar el repuesto'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (part) => {
    if (!window.confirm(`¿Eliminar el repuesto "${part.name}"?`)) return;
    try {
      await deleteITSparePart(part._id);
      toast.success('Repuesto eliminado');
      loadParts();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'No se pudo eliminar'));
    }
  };

  const handleMovement = async (event) => {
    event.preventDefault();
    const quantity = parseInt(movementForm.quantity, 10);
    if (!(quantity > 0)) return toast.error('La cantidad debe ser mayor que 0');
    try {
      const res = await createInventoryMovement({
        spare_part_id: movementPart._id,
        movement_type: movementForm.movement_type,
        quantity,
        reference: movementForm.reference.trim() || null,
        notes: movementForm.notes.trim() || null,
      });
      toast.success(`Movimiento registrado. Stock: ${res.data.new_stock}`);
      setMovementPart(null);
      loadParts();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'No se pudo registrar el movimiento'));
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Package className="text-[#3AB8D6]" size={28} /> Repuestos
            </h1>
            <p className="text-gray-400 text-sm mt-1">Inventario de repuestos y movimientos de stock</p>
          </div>
          <Button onClick={openCreate} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">
            <Plus size={18} className="mr-2" /> Nuevo Repuesto
          </Button>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <Input placeholder="Buscar por nombre, SKU, categoría..." value={search} onChange={(event) => setSearch(event.target.value)} className="max-w-sm bg-[#1a1d23] border-gray-700 text-white" />
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={lowStockOnly} onChange={(event) => setLowStockOnly(event.target.checked)} className="rounded" />
            Solo poco stock
          </label>
        </div>

        <div className="bg-[#1a1d23] border border-gray-800 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E2B05E]"></div>
            </div>
          ) : visibleParts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package size={48} className="mx-auto mb-3 opacity-50" />
              <p>No hay repuestos que coincidan</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800 text-xs text-gray-400 uppercase">
                    <th className="text-left p-3">Repuesto</th>
                    <th className="text-left p-3">Categoría</th>
                    <th className="text-right p-3">Stock</th>
                    <th className="text-right p-3">Costo</th>
                    <th className="text-left p-3">Ubicación</th>
                    <th className="text-right p-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleParts.map((part) => (
                    <tr key={part._id} className="border-b border-gray-800/50 hover:bg-[#12141a]">
                      <td className="p-3">
                        <div className="text-sm text-white font-medium">{part.name}</div>
                        <div className="text-xs text-gray-500">{part.sku || ''}</div>
                      </td>
                      <td className="p-3 text-sm text-gray-300">{part.category || '-'}</td>
                      <td className="p-3 text-right">
                        <span className="text-white font-semibold">{part.stock}</span>
                        <span className="text-xs text-gray-500"> / mín {part.minimum_stock}</span>
                        {isLowStock(part) && (
                          <Badge className="ml-2 bg-red-500/20 text-red-400"><Warning size={12} className="mr-1" />Bajo</Badge>
                        )}
                      </td>
                      <td className="p-3 text-right text-sm text-gray-300">${(Number(part.unit_cost) || 0).toFixed(2)}</td>
                      <td className="p-3 text-sm text-gray-400">{part.location || '-'}</td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" aria-label="Registrar movimiento" onClick={() => { setMovementPart(part); setMovementForm(EMPTY_MOVEMENT); }}>
                            <ArrowsLeftRight size={16} className="text-[#3AB8D6]" />
                          </Button>
                          <Button size="sm" variant="ghost" aria-label="Editar repuesto" onClick={() => openEdit(part)}>
                            <PencilSimple size={16} className="text-[#E2B05E]" />
                          </Button>
                          <Button size="sm" variant="ghost" aria-label="Eliminar repuesto" onClick={() => handleDelete(part)}>
                            <Trash size={16} className="text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#E2B05E]">{editing ? 'Editar Repuesto' : 'Nuevo Repuesto'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="text-gray-300">Nombre *</Label><Input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} className={inputClass} /></div>
                <div><Label className="text-gray-300">SKU</Label><Input value={form.sku} onChange={(event) => setForm((prev) => ({ ...prev, sku: event.target.value }))} className={inputClass} /></div>
                <div><Label className="text-gray-300">Categoría</Label><Input value={form.category} onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))} className={inputClass} /></div>
                <div><Label className="text-gray-300">Ubicación en almacén</Label><Input value={form.location} onChange={(event) => setForm((prev) => ({ ...prev, location: event.target.value }))} className={inputClass} /></div>
                {!editing && (
                  <div><Label className="text-gray-300">Stock inicial</Label><Input type="number" min="0" value={form.stock} onChange={(event) => setForm((prev) => ({ ...prev, stock: event.target.value }))} className={inputClass} /></div>
                )}
                <div><Label className="text-gray-300">Stock mínimo</Label><Input type="number" min="0" value={form.minimum_stock} onChange={(event) => setForm((prev) => ({ ...prev, minimum_stock: event.target.value }))} className={inputClass} /></div>
                <div><Label className="text-gray-300">Costo unitario</Label><Input type="number" min="0" step="0.01" value={form.unit_cost} onChange={(event) => setForm((prev) => ({ ...prev, unit_cost: event.target.value }))} className={inputClass} /></div>
              </div>
              {editing && <p className="text-xs text-gray-500">El stock ({editing.stock}) se cambia registrando movimientos.</p>}
              <div><Label className="text-gray-300">Notas</Label><Textarea value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} className={inputClass} rows={2} /></div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-gray-600 text-gray-300">Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">{saving ? 'Guardando…' : 'Guardar'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(movementPart)} onOpenChange={(open) => !open && setMovementPart(null)}>
          <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#E2B05E]">Movimiento — {movementPart?.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleMovement} className="space-y-4">
              <p className="text-sm text-gray-400">Stock actual: <strong className="text-white">{movementPart?.stock}</strong></p>
              <div>
                <Label className="text-gray-300">Tipo</Label>
                <Select value={movementForm.movement_type} onValueChange={(value) => setMovementForm((prev) => ({ ...prev, movement_type: value }))}>
                  <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1d23] border-gray-700">
                    {MOVEMENT_TYPES.map((type) => <SelectItem key={type.code} value={type.code}>{type.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-gray-300">Cantidad</Label><Input type="number" min="1" value={movementForm.quantity} onChange={(event) => setMovementForm((prev) => ({ ...prev, quantity: event.target.value }))} className={inputClass} /></div>
              <div><Label className="text-gray-300">Referencia</Label><Input value={movementForm.reference} onChange={(event) => setMovementForm((prev) => ({ ...prev, reference: event.target.value }))} className={inputClass} placeholder="Visita, factura, ticket…" /></div>
              <div><Label className="text-gray-300">Notas</Label><Textarea value={movementForm.notes} onChange={(event) => setMovementForm((prev) => ({ ...prev, notes: event.target.value }))} className={inputClass} rows={2} /></div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <Button type="button" variant="outline" onClick={() => setMovementPart(null)} className="border-gray-600 text-gray-300">Cancelar</Button>
                <Button type="submit" className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">Registrar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
